/**
 * Workspace settings for automated SMS payment reminders (Fast2SMS DLT).
 * Template (Message ID 14498 / sender TRIGVX):
 * Dear {#alp#}, a pending balance of Rs. {#num#} is to be paid against Ref ID: {#alp#} on date {#alp#}. Please ignore if already paid. -- TRIGOVEX
 */
import { useEffect, useState } from 'react';
import { loadDevJson, saveDevJson } from './devStorage';
import { readCompanyProfile, companyDisplayName } from './companyProfile';
import { resolveWorkspaceAsOfMs } from './workspaceAsOf';

export const SMS_REMINDER_KEY = 'sms_reminder_settings_v1';
const LEGACY_REMINDER_KEY = 'whatsapp_reminder_settings_v1';
const SETTINGS_EVENT = 'sms-reminder-settings';

export type SmsReminderSettings = {
  enabled: boolean;
  /** Days before due date to send a reminder, e.g. [1] or [7, 2, 1]. */
  reminderDaysBefore: number[];
};

export function emptySmsReminderSettings(): SmsReminderSettings {
  // Default: one day before EMI — admin can add more days.
  return { enabled: false, reminderDaysBefore: [1] };
}

function emit() {
  try {
    window.dispatchEvent(new Event(SETTINGS_EVENT));
  } catch {
    /* no window */
  }
}

function parseSettings(raw: Partial<SmsReminderSettings> | null): SmsReminderSettings {
  const base = emptySmsReminderSettings();
  if (!raw || typeof raw !== 'object') return base;
  const days = Array.isArray(raw.reminderDaysBefore)
    ? raw.reminderDaysBefore
        .map((d) => {
          const n = Math.floor(Number(d) || 0);
          return Math.max(1, Math.min(60, n));
        })
        .filter((d) => d > 0)
    : base.reminderDaysBefore;
  return {
    enabled: raw.enabled === true,
    reminderDaysBefore: [...new Set(days)].sort((a, b) => b - a),
  };
}

export function readSmsReminderSettings(): SmsReminderSettings {
  const raw = loadDevJson<Partial<SmsReminderSettings> | null>(SMS_REMINDER_KEY, null);
  if (raw) return parseSettings(raw);
  const legacy = loadDevJson<Partial<SmsReminderSettings> | null>(LEGACY_REMINDER_KEY, null);
  return parseSettings(legacy);
}

export function writeSmsReminderSettings(next: SmsReminderSettings): void {
  const days = [...new Set(next.reminderDaysBefore.map((d) => Math.max(1, Math.min(60, Math.floor(d)))))]
    .filter((d) => d > 0)
    .sort((a, b) => b - a);
  saveDevJson(SMS_REMINDER_KEY, { enabled: !!next.enabled, reminderDaysBefore: days });
  emit();
}

export function useSmsReminderSettings(): SmsReminderSettings {
  const [settings, setSettings] = useState(readSmsReminderSettings);
  useEffect(() => {
    const fn = () => setSettings(readSmsReminderSettings());
    window.addEventListener(SETTINGS_EVENT, fn);
    return () => window.removeEventListener(SETTINGS_EVENT, fn);
  }, []);
  return settings;
}

/** Normalize Indian mobile to digits (91XXXXXXXXXX). */
export function normalizeSmsPhone(phone: string): string {
  let digits = String(phone || '').replace(/\D/g, '');
  if (digits.length === 10) digits = `91${digits}`;
  if (digits.startsWith('0') && digits.length === 11) digits = `91${digits.slice(1)}`;
  return digits;
}

export function formatInr(amount: number): string {
  return `₹${Math.round(Math.max(0, Number(amount) || 0)).toLocaleString('en-IN')}`;
}

function formatSmsAmount(amount: number): string {
  return String(Math.round(Math.max(0, Number(amount) || 0)));
}

/** DLT date style: 25-10-2026 */
export function formatDueDateDlt(isoDate: string): string {
  const ymd = String(isoDate || '').trim().slice(0, 10);
  const m = ymd.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  if (!isoDate || isoDate === '—') return '—';
  const d = new Date(`${isoDate}T12:00:00`);
  if (!Number.isFinite(d.getTime())) return isoDate;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}-${mm}-${d.getFullYear()}`;
}

export function formatDueDateLabel(isoDate: string): string {
  return formatDueDateDlt(isoDate);
}

function sanitizeAlp(value: string, fallback = ''): string {
  const cleaned = String(value || '')
    .trim()
    .replace(/\|/g, ' ')
    .replace(/[^A-Za-z0-9\s\-./]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return cleaned || fallback;
}

/** Ref ID = HA number + "With" + Finance name (e.g. HP-101/2 With Teja Finance). */
export function buildSmsRefId(input: {
  haNumber?: string;
  financeName?: string;
  recordId?: string;
  kind?: 'finance' | 'handloan';
}): string {
  const finance = sanitizeAlp(input.financeName || companyDisplayName(readCompanyProfile()) || 'Finance', 'Finance');
  const ha = sanitizeAlp(input.haNumber || '');
  if (ha) return sanitizeAlp(`${ha} With ${finance}`, ha);
  const fallbackId =
    input.kind === 'handloan'
      ? `HL${String(input.recordId || '').replace(/[^A-Za-z0-9]/g, '').slice(-6)}`
      : `LN${String(input.recordId || '').replace(/[^A-Za-z0-9]/g, '').slice(-6)}`;
  return sanitizeAlp(`${fallbackId} With ${finance}`, fallbackId || 'LOAN');
}

/** Build the approved DLT payment reminder (preview + plain text). */
export function buildPaymentReminderMessage(input: {
  customerName: string;
  amount: number;
  dueDate: string;
  haNumber?: string;
  financeName?: string;
  refId?: string;
  recordId?: string;
  kind?: 'finance' | 'handloan';
  /** @deprecated ignored — kept so old callers still compile */
  businessName?: string;
  businessPhone?: string;
  facilityTitle?: string;
}): string {
  const name = sanitizeAlp(input.customerName || 'Customer', 'Customer');
  const amt = formatSmsAmount(input.amount);
  const finance = input.financeName || input.businessName || companyDisplayName(readCompanyProfile()) || 'Finance';
  const ref =
    sanitizeAlp(input.refId || '') ||
    buildSmsRefId({
      haNumber: input.haNumber,
      financeName: finance,
      recordId: input.recordId,
      kind: input.kind,
    });
  const due = formatDueDateDlt(input.dueDate);
  return `Dear ${name}, a pending balance of Rs. ${amt} is to be paid against Ref ID: ${ref} on date ${due}. Please ignore if already paid. -- TRIGOVEX`;
}

/** Days from workspace working date until due (0 = due on books date, negative = overdue). */
export function daysUntilDue(dueDateIso: string): number | null {
  if (!dueDateIso || dueDateIso === '—') return null;
  const due = new Date(`${dueDateIso}T12:00:00`);
  if (!Number.isFinite(due.getTime())) return null;
  const books = resolveWorkspaceAsOfMs({ allowWallClockFallback: true });
  const booksNoon = new Date(books);
  booksNoon.setHours(12, 0, 0, 0);
  return Math.round((due.getTime() - booksNoon.getTime()) / 86400000);
}
